import React from 'react';
import classNames from 'styles/utils/classNames';
import CountryFlag from 'components/CountryFlag';
import { CALENDAR_DATA_BY_DATE } from 'constants/stockApi';
import {
  CustomColumnDef,
  formatPercentage,
  getTextColorClass,
  calculateSurprisePercentage,
  formatNumber,
  getTextColorClassPercent,
  calculateChangePercentage,
} from 'screens/Calendar';

const getCountryCode = (row: any): string | null => {
  if (row.countryCode) return row.countryCode;

  if (row.currency && CALENDAR_DATA_BY_DATE[row.currency]) {
    return CALENDAR_DATA_BY_DATE[row.currency];
  }

  return null;
};

export const createEconomicEventsColumns = (): CustomColumnDef<any>[] => [
  {
    accessorKey: 'date',
    header: 'Date',
    csvType: 'date',
  },
  {
    accessorKey: 'country',
    header: 'Country',
    cell: ({ row }) => {
      const countryName = row.original.country;
      const countryCode = getCountryCode(row.original);

      return (
        <div className="flex items-center space-x-2">
          {countryCode && (
            <div className="h-5 w-5 flex-shrink-0 overflow-hidden rounded-full border border-gray-600">
              <CountryFlag
                code={countryCode}
                className="h-full w-full object-cover"
                fallback={<span className="text-xs">🏳️</span>}
              />
            </div>
          )}
          <span className="whitespace-nowrap">{countryName}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'event',
    header: 'Event',
  },
  {
    accessorKey: 'currency',
    header: 'Currency',
    csvType: 'currency',
  },
  {
    accessorKey: 'previous',
    header: 'Previous',
    csvType: 'number',
    cell: ({ row }) => {
      const previous = row.original.previous;
      if (!previous) return '--';

      return <span>{previous}</span>;
    },
  },
  {
    accessorKey: 'estimate',
    header: 'Forecast',
    csvType: 'number',
    cell: ({ row }) => {
      const estimate = row.original.estimate;
      if (!estimate) return '--';
      return <span>{estimate}</span>;
    },
  },
  {
    header: 'Actual',
    accessorKey: 'actual',
    cell: ({ row }) => {
      const actual = row.original.actual;
      if (!actual) return '--';
      return <span>{actual}</span>;
    },
  },
  {
    header: 'Change',
    id: 'change',
    accessorFn: (row) => {
      if (row.actual == null || row.previous == null) return null;
      const actual = Number(row.actual);
      const previous = Number(row.previous);
      if (!isFinite(actual) || !isFinite(previous)) return null;
      return actual - previous;
    },
    cell: ({ getValue }) => {
      const change = getValue() as number | null;
      return change == null ? (
        '--'
      ) : (
        <span className={getTextColorClass(change, 1)}>{formatNumber(change, 1)}</span>
      );
    },
  },
  {
    id: 'changePercentage',
    header: 'Change %',
    accessorFn: (row) => calculateChangePercentage(row.actual, row.previous),
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      if (value == null) {
        return <span className="text-gray-400">--</span>;
      }

      return <span className={getTextColorClassPercent(value)}>{formatPercentage(value, 1)}</span>;
    },
    csvType: 'percentage-formatted',
  },
  {
    id: 'surprise',
    accessorFn: (row) => {
      const { actual, estimate } = row;
      if (actual == null || estimate == null) return null;
      return actual - estimate;
    },
    header: 'Surprise',
    cell: ({ getValue }) => {
      const surprise = getValue() as number | null;
      if (surprise == null) return '--';
      return <span className={getTextColorClass(surprise, 1)}>{formatNumber(surprise, 1)}</span>;
    },
  },
  {
    id: 'surprisePercentage',
    header: 'Surprise %',
    csvType: 'percentage-formatted',
    accessorFn: (row) => calculateSurprisePercentage(row.actual, row.estimate),
    cell: ({ getValue }) => {
      const value = getValue() as number;

      if (value == null) {
        return <span className="text-gray-400">--</span>;
      }

      return <span className={getTextColorClassPercent(value)}>{formatPercentage(value, 1)}</span>;
    },
  },
  {
    accessorKey: 'impact',
    header: 'Impact',
    cell: ({ row }) => {
      const impact = row.original.impact || '--';
      return (
        <span
          className={classNames(
            'rounded px-2 py-1',
            impact === 'High'
              ? 'text-red-500'
              : impact === 'Medium'
              ? 'text-yellow-200'
              : impact === 'Low'
              ? 'text-green-500'
              : 'text-white',
          )}
        >
          {impact}
        </span>
      );
    },
  },
];
